import * as React from 'react';

import {getHumanReadableEntityTypeFromGraphQLEnum} from 'nutshell-core/utils';

import type {EntityType} from 'nutshell-graphql-types';

import {GraphQLSelectInline} from './graphql-select-inline';
import type {Option} from './graphql-select-inline';
import {useGetSelectableCustomFields} from './hooks/use-get-selectable-custom-fields';

type Props = {
    entityType: EntityType;
    onChange: (option: Option | Option[]) => void;
    selectedValues: Option[];
    isMulti?: boolean;
    controlShouldRenderValue?: boolean;
    clearable?: boolean;
    disabled?: boolean;
};

export function GraphQLSelectCustomFields(props: Props) {
    const {selectedValues, entityType} = props;

    const {customFields} = useGetSelectableCustomFields(entityType);

    const customFieldOptions = React.useMemo(
        () =>
            customFields
                ? customFields.map((field) => ({
                      id: field.id,
                      name: field.attribute.name,
                      // Description is used for display in the selector, type is used to
                      // pass the value back onChange
                      description: field.attribute.displayType,
                      type: field.attribute.displayType,
                  }))
                : [],
        [customFields]
    );

    const noOptionsMessage = React.useCallback(
        ({inputValue}) => {
            if (!customFieldOptions.length) {
                const entityName =
                    getHumanReadableEntityTypeFromGraphQLEnum(entityType).toLowerCase();

                return `No ${entityName} custom fields available`;
            }

            if (inputValue.length) {
                return `Nothing found matching '${inputValue}'`;
            }

            if (selectedValues.length) {
                return 'Type to search for custom fields…';
            }

            return null;
        },
        [customFieldOptions, selectedValues, entityType]
    );

    return (
        <GraphQLSelectInline
            placeholder='Type to search for custom fields…'
            value={props.selectedValues}
            defaultOptions={customFieldOptions}
            // Dummy load options to satisfy the select component
            loadOptions={() => {
                return Promise.resolve(customFieldOptions);
            }}
            onChange={props.onChange}
            multi={props.isMulti}
            noOptionsMessage={noOptionsMessage}
            labelKey='name'
            valueKey='id'
            onBlur={() => {}}
            clearable={props.clearable}
            disabled={props.disabled}
            controlShouldRenderValue={props.controlShouldRenderValue}
            searchable={true}
            hasDropdownIndicator={true}
            isTwoLine={true}
            openMenuOnClick={true}
        />
    );
}
